import { useMutation } from '@apollo/client';
import { Button, Icon, SettingsGroup, SettingsItemTextField, Tooltip, useTable } from '@elipssolution/harfang';
import { mdiEmailArrowRight, mdiPencil, mdiPlus } from '@mdi/js';
import { Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import AddCustomerFilesTable from './AddCustomerFilesTable';
import { useConfirmationDialogContext } from '../../../../components/ConfirmationDialogProvider';
import { useSettingsDialog } from '../../../../hooks/useSettingsDialog';
import { CustomerFileWithGroup } from '../../../../types/customerFile';
import { generateErrorInformations } from '../../../../utils/errorHandler';
import { renderFullName } from '../../../../utils/renderFullName';
import { SUCCESS_ANIMATION_DELAY } from '../../../../utils/statusAnimationDelay';
import {
	REMOVE_EXTERNAL_USER,
	SEND_EXTERNAL_USER_ACCOUNT_CONFIRMATION_MAIL,
	UPDATE_EXTERNAL_USER,
	UpdateExternalUserType,
} from '../../../api/externalUser';
import { useSession } from '../../../components/session/SessionContext';
import { AddExternalUserRelationWithCustomerFilesType, ExternalUserType } from '../../../types/user';
import SettingsItemEmailField from '../../SettingsItemEmailField';

type FormType = {
	firstName?: ExternalUserType['firstName'];
	lastName?: ExternalUserType['lastName'];
	email?: ExternalUserType['email'];
};

type ExternalUserFormProps = {
	user?: ExternalUserType;
	isUserCreationSucceeded: boolean;
	onSave: (userWithCustomerFiles: AddExternalUserRelationWithCustomerFilesType) => void;
	onError: (error: string) => void;
	onUserUpdate: (updatedUser: ExternalUserType) => void;
};

const ExternalUserForm = ({ user, isUserCreationSucceeded, onSave, onError, onUserUpdate }: ExternalUserFormProps) => {
	const { id, lastName, firstName, email: initialUserEmail } = user ?? {};

	const customerFileTableInstance = useTable();
	const { back, onHasDirtyFormChange, onCloseConfirmationTitleChange } = useSettingsDialog();
	const { user: sessionUser } = useSession();
	const { confirmAction } = useConfirmationDialogContext();

	const {
		control,
		formState: { dirtyFields, isDirty, isValid },
		handleSubmit,
		reset,
	} = useForm<FormType>({
		mode: 'onChange',
	});

	const [selectedCustomerFiles, setSelectedCustomerFiles] = useState<CustomerFileWithGroup[]>([]);

	const isUserSessionUser = useMemo(() => sessionUser.id === id, [id, sessionUser.id]);

	const [
		updateExternalUser,
		{
			data: updateExternalUserData,
			loading: isUpdateExternalUserLoading,
			reset: resetUpdateExternalUserMutationStatus,
		},
	] = useMutation<UpdateExternalUserType>(UPDATE_EXTERNAL_USER, {
		onCompleted: ({ updateExternalUser: updatedExternalUser }) => {
			onUserUpdate(updatedExternalUser);

			setTimeout(() => resetUpdateExternalUserMutationStatus(), SUCCESS_ANIMATION_DELAY);
		},
		onError: (mutationError) => {
			onError(generateErrorInformations({ error: mutationError, resource: 'updateExternalUser' }).message);
		},
	});

	const [removeExternalUser, { loading: isRemoveExternalUserLoading }] = useMutation(REMOVE_EXTERNAL_USER, {
		onCompleted: back,
		onError: (mutationError) => {
			onError(generateErrorInformations({ error: mutationError, resource: 'removeExternalUser' }).message);
		},
	});

	const [sendExternalUserAccountConfirmationMail] = useMutation(SEND_EXTERNAL_USER_ACCOUNT_CONFIRMATION_MAIL);

	const isLoading = useMemo(
		() => isRemoveExternalUserLoading || isUpdateExternalUserLoading,
		[isRemoveExternalUserLoading, isUpdateExternalUserLoading],
	);

	const isUserSelectedUserConnected = useMemo(() => id === sessionUser.id, [id, sessionUser]);

	const isMutationButtonDisabled = useMemo(() => isLoading || !isDirty || !isValid, [isLoading, isDirty, isValid]);

	const startIcon = useMemo(() => <Icon path={id ? mdiPencil : mdiPlus} />, [id]);

	const handleDeleteClick = async () => {
		const isDeleteConfirmed = await confirmAction({
			title: `Êtes-vous sûr de vouloir supprimer l'utilisateur ${renderFullName({
				firstName,
				lastName: lastName ?? '',
			})} ?`,
			confirmButtonDetails: { color: 'error', label: 'Supprimer' },
		});

		return isDeleteConfirmed ? removeExternalUser({ variables: { id } }) : null;
	};

	const handleSendAccountConfirmationMail = useCallback(
		() => sendExternalUserAccountConfirmationMail({ variables: { externalUserId: id } }),
		[sendExternalUserAccountConfirmationMail, id],
	);
	const handleSelectedCustomerFilesChange = (newSelectedCustomerFiles: CustomerFileWithGroup[]) =>
		setSelectedCustomerFiles(newSelectedCustomerFiles);

	const onSubmit = useCallback(
		async (values: FormType) => {
			const { firstName: formFirstName, lastName: formLastName = '', email: formEmail = '' } = values;

			if (!id) {
				reset({
					email: undefined,
					lastName: undefined,
					firstName: undefined,
				});

				handleSelectedCustomerFilesChange([]);
				customerFileTableInstance.reload();

				return onSave({
					user: {
						firstName: formFirstName,
						lastName: formLastName,
						email: formEmail,
					},
					customerFiles: selectedCustomerFiles,
				});
			}

			return updateExternalUser({
				variables: {
					updateExternalUserInput: {
						...values,
						firstName: formFirstName || null,
						id,
					},
				},
			});
		},
		[id, onSave, selectedCustomerFiles, reset, customerFileTableInstance, updateExternalUser],
	);

	const handleSubmitClick = useCallback(async () => {
		if (id && dirtyFields.email) {
			const isEmailModificationConfirmed = await confirmAction({
				title: `Êtes-vous sûr de vouloir modifier l'utilisateur ${renderFullName({
					firstName,
					lastName: lastName ?? '',
				})} ?`,
				content: `Vous avez modifié l'adresse mail de l'utilisateur.
					Cette modification va également modifier l'identifiant de connexion de l'utilisateur ce qui pourrait l'empêcher de se connecter.

					Assurez-vous d'avoir communiqué cette modification avant de confirmer.`,
				confirmButtonDetails: { label: 'Confirmer la modification' },
			});

			if (!isEmailModificationConfirmed) {
				return null;
			}
		}

		return handleSubmit(onSubmit)();
	}, [id, dirtyFields.email, handleSubmit, onSubmit, confirmAction, firstName, lastName]);

	const submitButtonLabel = useMemo(() => {
		if (isUserCreationSucceeded) return 'Utilisateur ajouté';
		if (id) {
			if (updateExternalUserData) return 'Utilisateur modifié';

			return 'Modifier';
		}

		return 'Ajouter';
	}, [isUserCreationSucceeded, id, updateExternalUserData]);

	// Setting form state
	useEffect(
		() =>
			reset({
				email: initialUserEmail,
				lastName,
				firstName,
			}),
		[reset, initialUserEmail, lastName, firstName],
	);

	useEffect(() => {
		onHasDirtyFormChange(isDirty);
		onCloseConfirmationTitleChange(
			`Êtes-vous sûr de vouloir quitter la ${user ? 'modification' : 'création'} de l'utilisateur ?`,
		);
	}, [isDirty, onCloseConfirmationTitleChange, onHasDirtyFormChange, user]);

	return (
		<>
			<SettingsGroup>
				<Controller
					control={control}
					render={({ field: { onChange, ...field } }) => {
						const handleUpperCaseChange = (value?: string) => {
							onChange(value?.toUpperCase());
						};

						return (
							<SettingsItemTextField
								{...field}
								description="Nom de l'utilisateur."
								label="Nom"
								onChange={handleUpperCaseChange}
								required
							/>
						);
					}}
					name="lastName"
					rules={{ required: true }}
				/>
				<Controller
					control={control}
					render={({ field: { onChange, ...field } }) => {
						const handleFirstLetterUpperCaseChange = (value?: string) => {
							onChange(value ? value.charAt(0).toUpperCase() + value.slice(1) : undefined);
						};

						return (
							<SettingsItemTextField
								{...field}
								description="Prénom de l'utilisateur."
								label="Prénom"
								onChange={handleFirstLetterUpperCaseChange}
							/>
						);
					}}
					name="firstName"
				/>
				<SettingsItemEmailField<FormType>
					control={control}
					initialValue={initialUserEmail}
					disabled={isUserSessionUser}
				/>
			</SettingsGroup>

			{!user && (
				<AddCustomerFilesTable
					selectedCustomerFiles={selectedCustomerFiles}
					onSelectedCustomerFilesChange={handleSelectedCustomerFilesChange}
					tableInstance={customerFileTableInstance}
				/>
			)}

			<Stack flexDirection="row-reverse" justifyContent="space-between">
				<Stack flexDirection="row" gap={1}>
					{id && !isUserSessionUser && (
						<Tooltip
							content={
								!!dirtyFields.email &&
								"Un changement est en cours sur l'adresse email. Vous devez valider le changement ou l'annuler avant de pouvoir renvoyer un email de confirmation à l'utilisateur"
							}
						>
							<Button
								disabled={!!dirtyFields.email}
								onClick={handleSendAccountConfirmationMail}
								startIcon={<Icon path={mdiEmailArrowRight} />}
							>
								Renvoyer la confirmation
							</Button>
						</Tooltip>
					)}

					<Button
						disabled={isMutationButtonDisabled}
						onClick={handleSubmitClick}
						startIcon={startIcon}
						variant="contained"
					>
						{submitButtonLabel}
					</Button>
				</Stack>

				{id && (
					<Tooltip
						content={isUserSelectedUserConnected ? 'Un utilisateur ne peut pas se supprimer lui même' : undefined}
					>
						<Button
							color="error"
							disabled={isLoading || isUserSelectedUserConnected}
							onClick={handleDeleteClick}
							variant="outlined"
						>
							Supprimer
						</Button>
					</Tooltip>
				)}
			</Stack>
		</>
	);
};

export default ExternalUserForm;
